import React, { createContext, PropsWithChildren, useContext, useState } from 'react';
import { EchoChamber, EchoChamberSong } from '~/components/EchoChamber/EchoChamber';

export interface EchoChamberContextType {
  echoChamberSong: EchoChamberSong | null;
  setEchoChamberSong: ( song: EchoChamberSong | null ) => void;
  wantsToPlay: boolean;
  setWantsToPlay: ( wantsToPlay: boolean ) => void;
  playlist: EchoChamberSong[];
  setPlaylist: ( playlist: EchoChamberSong[] ) => void;
  currentIndex: number;
  setCurrentIndex: ( index: number ) => void;
  isPlaying: boolean;
  setIsPlaying: ( playing: boolean ) => void;
  currentTime: number;
  setCurrentTime: ( time: number ) => void;
  isOpen: boolean;
  setIsOpen: ( isOpen: boolean ) => void;
}

const defaultEchoChamberProps: EchoChamberContextType = {
  echoChamberSong: null,
  setEchoChamberSong: () => {},
  wantsToPlay: false,
  setWantsToPlay: () => {},
  playlist: [],
  setPlaylist: () => {},
  currentIndex: 0,
  setCurrentIndex: () => {},
  isPlaying: false,
  setIsPlaying: () => {},
  currentTime: 0,
  setCurrentTime: () => {},
  isOpen: false,
  setIsOpen: () => {},
};

export const EchoChamberContext = createContext<EchoChamberContextType>( defaultEchoChamberProps );

export const EchoChamberContextProvider: React.FC<PropsWithChildren<{}>> = ( { children } ) => {
  const [echoChamberSong, setEchoChamberSong] = useState<EchoChamberSong | null>( null );
  const [wantsToPlay, setWantsToPlay] = useState<boolean>( false );
  const [playlist, setPlaylist] = useState<EchoChamberSong[]>( [] );
  const [currentIndex, setCurrentIndex] = useState<number>( 0 );
  const [isPlaying, setIsPlaying] = useState<boolean>( false );
  const [currentTime, setCurrentTime] = useState<number>( 0 );
  const [isOpen, setIsOpen] = useState<boolean>( false );

  return (
    <EchoChamberContext.Provider
      value={ {
        echoChamberSong,
        setEchoChamberSong,
        wantsToPlay,
        setWantsToPlay,
        playlist,
        setPlaylist,
        currentIndex,
        setCurrentIndex,
        isPlaying,
        setIsPlaying,
        currentTime,
        setCurrentTime,
        isOpen,
        setIsOpen,
      } }
    >
      { children }
    </EchoChamberContext.Provider>
  );
};

export const useEchoChamberContext = (): EchoChamberContextType => {
  const context = useContext( EchoChamberContext );
  if ( !context ) {
    throw new Error( 'useEchoChamberContext must be used within a <EchoChamberContextProvider>' );
  }
  return context;
};

export const GlobalEchoChamber: React.FC = () => {
  const {
    currentIndex,
    playlist,
    setCurrentIndex,
    setCurrentTime,
    setEchoChamberSong,
    setIsPlaying,
    setWantsToPlay,
    wantsToPlay,
    setIsOpen,
  } = useEchoChamberContext();

  if ( !playlist.length || currentIndex < 0 || currentIndex >= playlist.length ) {
    return null;
  }

  const selectPrevious = () => {
    if ( currentIndex > 0 ) {
      const newIndex = currentIndex - 1;
      setCurrentIndex( newIndex );
      setEchoChamberSong( playlist[newIndex] );
    }
  };

  const selectNext = () => {
    if ( currentIndex < playlist.length - 1 ) {
      const newIndex = currentIndex + 1;
      setCurrentIndex( newIndex );
      setEchoChamberSong( playlist[newIndex] );
    }
  };

  return (
    <EchoChamber
      song={ playlist[currentIndex] }
      selectPrevious={ currentIndex > 0 ? selectPrevious : undefined }
      selectNext={ currentIndex < playlist.length - 1 ? selectNext : undefined }
      wantsToPlay={ wantsToPlay }
      onWantsToPlayChange={ setWantsToPlay }
      setContextCurrentTime={ setCurrentTime }
      setContextIsPlaying={ setIsPlaying }
      setContextIsOpen={ setIsOpen }
    />
  );
};
